import { Injectable } from '@tanbo/vue-di-plugin'
import type { ComposerTranslation } from 'vue-i18n'

import { Http } from '@/services/http'
import { getAreaByTerminalType } from '@/services/service-permission.service'
import { ClientItem } from '@/views/dashboard/_type/client-item'
import { ParamsClean } from '@/common/decorators/params-clean'
import { isVersionGte } from '@/utils/tool'
import { TerminalType, TerminalTypeLabel } from '@/views/dashboard/_enum/terminal-type'
import { decryptWebSocketUrl } from '@/views/dashboard/group/client/modals/remote-assist/decrypt-url'
import { IS_SERVER } from '@/common/const/env'
import { sshDeviceStore, vncDeviceStore } from '@/services/remote-device.service'

/** 支持vnc的客户端最低版本 */
export const VNC_CVERSION = '1.3.0.0'
/** 支持ssh的客户端最低版本 */
export const SSH_CVERSION = '1.4.0.0'

/** 各操作系统远控配置 */
export const OS_REMOTE: Record<number, OsRemoteItem> = {
  /** 新加入的客户端上报需要几秒钟，此时后端会返回system_type为0 */
  0: {} as OsRemoteItem,
  [TerminalType.windows]: {
    has_old: !IS_SERVER,
    has_vnc: true,
    has_ssh: false,
    vnc_cversion: VNC_CVERSION,
    ssh_cversion: SSH_CVERSION,
  },
  [TerminalType.linux]: {
    has_old: false,
    has_vnc: true,
    has_ssh: true,
    vnc_cversion: VNC_CVERSION,
    ssh_cversion: SSH_CVERSION,
  },
  [TerminalType.windowsServer]: {
    has_old: !IS_SERVER,
    has_vnc: true,
    has_ssh: false,
    vnc_cversion: VNC_CVERSION,
    ssh_cversion: SSH_CVERSION,
  },
  [TerminalType.mac]: {
    has_old: false,
    has_vnc: false,
    has_ssh: false,
    vnc_cversion: VNC_CVERSION,
    ssh_cversion: SSH_CVERSION,
  },
}

/** vnc local storage debug开关， */
export const DEBUG_REMOTE = 'DEBUG_REMOTE'

export enum REMOTE_STATE {
  UNINITIALIZED = 0, // 状态未初始化，前端自设状态
  IDLE = 1, // 可以远程连接
  WAITING, // 等待终端响应
  READY, // 终端已就绪
  CONNECTING, // 终端已连接
  DISCONNECTED, // 已断开
  CAN_NOT_CONNECT, // 无法连接
  OTHERS_CONNECTING, // 与其他管理员连接中
}

export const REMOTE_TYPE = {
  REMOTE_ASSIST: 1,
  REMOTE_LOGIN: 2,
  REMOTE_SSH: 3,
}

/** 远程类型卡片配置接口 */
export interface RemoteTypeCard {
  /** 远程类型ID */
  type: number
  /** 卡片图标路径 */
  icon: any
  /** 卡片标题 */
  title: string
  /** 卡片描述 */
  desc: string
  /** 是否仅支持桌面版系统 */
  onlyDesktop: boolean
  /** 禁用文案 */
  disableText?: string
}

/** 远程类型卡片（无文案，标题/描述由 i18n 在组件内拼接） */
export interface RemoteTypeCardMeta {
  type: number
  icon: any
  onlyDesktop: boolean
  /** 是否通过 getSvgIconDataUrl 解析图标（用于支持主题色切换的 SVG 图标） */
  resolveSvg?: boolean
}

export const REMOTE_TYPE_CARD_META: RemoteTypeCardMeta[] = [
  {
    type: REMOTE_TYPE.REMOTE_ASSIST,
    icon: 'views/dashboard/group/client/modals/_images/r-assist',
    onlyDesktop: true,
    resolveSvg: true,
  },
  {
    type: REMOTE_TYPE.REMOTE_LOGIN,
    icon: 'views/dashboard/group/client/modals/_images/r-control',
    onlyDesktop: true,
    resolveSvg: true,
  },
  {
    type: REMOTE_TYPE.REMOTE_SSH,
    icon: require('../_images/r_ssh.png'),
    onlyDesktop: false,
  }
]

export interface ConnectStateReq {
  /** 受控端设备id */
  device_id: string
  /** 远程类型，1：远程协助，2：远程登录, 3：SSH远程 */
  remote_type?: number
}

export interface TurnOnReq {
  /** 受控端设备id */
  device_id: string
  password?: string
}

export interface TurnOffReq {
  /** 受控端设备id */
  device_id: string
  /** 受控设备连接token */
  token?: string
  room_id?: string
}

export interface OsRemoteItem {
  /** 是否支持旧版远控 */
  has_old: boolean
  /** 是否支持vnc远控 */
  has_vnc: boolean
  /** 是否支持ssh远控 */
  has_ssh: boolean
  /** 支持vnc远控的最低客户端版本，has_vnc为true才有效 */
  vnc_cversion: string
  /** 支持ssh远控的最低客户端版本，has_ssh为true才有效 */
  ssh_cversion: string
}

export function getColorDepthLabels (t: ComposerTranslation): Record<number, string> {
  return {
    8: t('groupClient.remoteAssistColor8'),
    16: t('groupClient.remoteAssistColor16'),
    32: t('groupClient.remoteAssistColor32'),
    64: t('groupClient.remoteAssistColor64'),
  }
}

export interface ConnectStateRes {
  /** 终端连接状态, 1-可以远程连接,2-等待终端响应,3-终端已就绪,4-终端已连接 */
  connect_status: number
  /** 终端连接token，只有连接状态为终端已就绪时才非空 */
  token: string
  /** 是否已完成 */
  is_finish: boolean
  /** 绑定密钥 */
  sk: string
  /** frp服务地址 */
  frp_endpoint: string
  /** 连接端口 */
  connect_port: number
  /** 主控端版本号 */
  visitor_version: string
  /** 远程登录端口 */
  remote_port: number
  encrypt_key?: string
  nonce?: string
  ciphertext?: string
  /** 远程控制的ws地址 前端字段 */
  ws_url?: string
}

export interface DeviceInfoReq {
  device_id?: string,
  permission_names?: string[]
  group_id?: number
}

export interface VncDevice {
  unload_timestamp?: number
  /** 设备id 作为主键 */
  device_id: string
  token: string
  room_id: string
  /** 远程类型，1：远程协助，2：远程登录 */
  remote_type: number
  ws_url: string
}

@Injectable()
export class RemoteAssistService {
  constructor (private http: Http) {}

  getDeviceInfo (data: DeviceInfoReq) {
    return this.http.post<{
      terminal_list: ClientItem[]
    }>('/terminal/list', data).then(res => {
      res.terminal_list.forEach(v => v.area_type = getAreaByTerminalType(v.system_type))
      return res
    })
  }

  getConnectState (data: ConnectStateReq) {
    return this.http.post<ConnectStateRes>('/remote-assist/get-state', data).then(async res => {
      if (res.encrypt_key && res.nonce && res.ciphertext) {
        res.ws_url = await decryptWebSocketUrl({ ciphertext: res.ciphertext, nonce: res.nonce, encrypt_key: res.encrypt_key })
      } else {
        res.ws_url = ''
      }
      return res
    })
  }

  @ParamsClean()
  turnOffRemoteAssist (data: TurnOffReq) {
    return this.http.post<void>('/remote-assist/turn-off', data)
  }

  turnOnRemoteAssist (data: TurnOnReq) {
    return this.http.post<void>('/remote-assist/turn-on', data)
  }

  openRemoteLoginWindow (params: {
    deviceId: string,
    login: {
      username: string,
      password: string,
      port: number
      colorDepth?: number,
      enableTheming?: boolean,
      enableWallpaper?: boolean,
      enableFullWindowDrag?: boolean,
    }
  }) {
    const { username, password, port, colorDepth, enableTheming, enableWallpaper, enableFullWindowDrag } = params.login
    if (!username || !password) {
      return
    }
    localStorage.setItem(`remote-login-${params.deviceId}`, JSON.stringify({
      username: username.trim(),
      password: password.trim(),
      port,
      colorDepth,
      enableTheming,
      enableWallpaper,
      enableFullWindowDrag,
    }))
    return window.open(`/remote-control?device_id=${params.deviceId}`, 'remoteControlWindow', `popup,width=${window.screen.availWidth - 10},height=${window.screen.availHeight - 10}`)
  }

  @ParamsClean()
  turnOffRemoteLogin (data: TurnOffReq) {
    return this.http.post<void>('/remote-login/turn-off', data)
  }

  turnOnRemoteLogin (data: TurnOnReq) {
    return this.http.post<void>('/remote-login/turn-on', data)
  }

  @ParamsClean()
  turnOffSsh (data: TurnOffReq) {
    return this.http.post<void>('/remote-ssh/turn-off', data)
  }

  turnOnSsh (data: TurnOnReq) {
    return this.http.post<void>('/remote-ssh/turn-on', data)
  }

  /** 终端上报连接心跳，上报后服务端将连接状态改为连接成功 */
  reportHeartbeat (device_id: string) {
    return this.http.post<void>('/remote-assist/connect/report-heartbeat', { device_id })
  }

  /**
   * 打开远程窗口（通用方法）
   */
  private openRemoteWindow(wsurl: string, path: string, deviceStore: typeof vncDeviceStore | typeof sshDeviceStore) {
    const top = window.screen.height / 2 - 250
    const left = window.screen.width / 2 - 450
    const urlObj = new URL(wsurl)
    const deviceId = urlObj.searchParams.get('d').replace(/^noVnc-/, '')
    deviceStore.replaceDevice(parseConnectInfo(wsurl))
    return window.open(
      `${path}?device_id=${deviceId}`,
      deviceId,
      `popup, status=no, location=no, toolbar=no, menubar=no, width=900, height=500, top=${top}, left=${left}`
    )
  }

  openVncWindow(wsurl: string) {
    return this.openRemoteWindow(wsurl, '/remote-vnc', vncDeviceStore)
  }

  openSshWindow(wsurl: string) {
    return this.openRemoteWindow(wsurl, '/remote-ssh', sshDeviceStore)
  }
}

export function parseConnectInfo (wsurl?: string): VncDevice {
  if (!wsurl) {
    return {} as any
  }
  const wsurlObj = new URL(wsurl)
  return {
    device_id: wsurlObj.searchParams.get('d').replace(/^noVnc-/, ''),
    token: wsurlObj.searchParams.get('tk'),
    room_id: wsurlObj.pathname.split('/').slice(-1)[0],
    remote_type: +wsurlObj.searchParams.get('rt'),
    ws_url: wsurl,
  }
}

/** VideoDecoder条件：安全上下文(https、设置信任) + 新版本浏览器  */
export const NO_VIDEO_DECODER = (window as any).VideoDecoder === undefined
/** subtle条件：安全上下文(https、设置信任) */
export const NO_SUBTLE = (window as any).crypto?.subtle === undefined

export const IS_HTTP = window.location.protocol.toLowerCase() === 'http:'

/**
 * 通过客户端版本号和系统类型判断是否支持 VNC 功能
 * @param {string} systemVersion - 客户端版本号
 * @param {TerminalType} systemType - 客户端操作系统类型
 */
export function isClientSupportVnc (systemVersion: string, systemType: TerminalType) {
  return isVersionGte(systemVersion, OS_REMOTE[systemType].vnc_cversion)
}

export function isClientSupportSsh (systemVersion: string, systemType: TerminalType) {
  return isVersionGte(systemVersion, OS_REMOTE[systemType].ssh_cversion)
}

export function getCardDisableText (typeConfig: RemoteTypeCardMeta, terminalInfo: ClientItem, t: ComposerTranslation): string | undefined {
  const { is_desktop, system_type, version } = terminalInfo
  const osConfig = OS_REMOTE[system_type]
  if (typeConfig.onlyDesktop && !Boolean(is_desktop)) {
    return t('groupClient.remoteAssistDisableDesktopOnly')
  }
  if ([REMOTE_TYPE.REMOTE_ASSIST, REMOTE_TYPE.REMOTE_LOGIN].includes(typeConfig.type)) {
    if (!isClientSupportVnc(version, system_type) && !osConfig.has_old) {
      return t('groupClient.remoteAssistTipClientVersionRequired', { type: TerminalTypeLabel[system_type], version: osConfig.vnc_cversion })
    }
  }
  if ([REMOTE_TYPE.REMOTE_SSH].includes(typeConfig.type)) {
    if (!osConfig.has_ssh) {
      const supportedSystems = Object.keys(OS_REMOTE).filter(k => OS_REMOTE[Number(k)].has_ssh)
      const systems = supportedSystems.map(v => TerminalTypeLabel[Number(v)]).join('、')
      return t('groupClient.remoteAssistSshSupportedSystemsOnly', { systems })
    }
    if (!isClientSupportSsh(version, system_type)) {
      return t('groupClient.remoteAssistTipClientVersionRequired', { type: TerminalTypeLabel[system_type], version: osConfig.ssh_cversion })
    }
  }
}
